package or.garden.manager.features.auth

import androidx.compose.runtime.*
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.browser.localStorage
import or.garden.manager.common.GardenHandler
import or.garden.manager.components.SnackbarType
import or.garden.manager.domain.AuthService
import or.garden.manager.domain.GardenHttpClient
import or.garden.manager.domain.model.GardenUserRequest
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.*

@Composable
fun LoginScreen(
    onLoginSuccess: () -> Unit,
    showSnackbar: (String, SnackbarType) -> Unit = { _, _ -> }
) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var isLogin by remember { mutableStateOf(true) }

    Div({
        classes("min-h-screen", "flex", "bg-emerald-50")
    }) {
        // Lijeva strana - Slika/Brending
        Div({
            classes("hidden", "lg:flex", "lg:w-1/2", "relative", "overflow-hidden")
        }) {
            Img(
                src = "https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
                attrs = {
                    classes("absolute", "inset-0", "w-full", "h-full", "object-cover")
                }
            )
            Div({
                classes(
                    "absolute", "inset-0", "bg-emerald-900/40", "p-20", "flex", "flex-col", "justify-between"
                )
            }) {
                Div({ classes("flex", "items-center", "gap-4") }) {
                    Div({
                        classes(
                            "w-16", "h-16", "bg-emerald-500", "rounded-full", "flex", "items-center", "justify-center", "shadow-lg"
                        )
                    }) {
                        I({ classes("fas", "fa-seedling", "text-white", "text-2xl") })
                    }
                    H1({ classes("text-4xl", "font-bold", "text-white") }) {
                        Text("Garden Manager")
                    }
                }

                Div {
                    H2({
                        classes("text-6xl", "font-black", "text-white", "mb-4", "leading-tight")
                    }) {
                        Text("Uzgajajte svoje")
                        Br()
                        Span({ classes("text-emerald-300") }) { Text("Snove") }
                    }
                    P({ classes("text-emerald-50", "text-xl", "max-w-md") }) {
                        Text("Planirajte, rotirajte i uzgajajte vlastito povrće s lakoćom.")
                    }
                }

                P({ classes("text-emerald-200", "text-sm") }) {
                    Text("© 2024 Garden Manager. Sva prava zadržana.")
                }
            }
        }

        // Desna strana - Forma
        Div({
            classes("w-full", "lg:w-1/2", "flex", "flex-col", "justify-center", "items-center", "p-8", "sm:p-12")
        }) {
            Div({
                classes("max-w-md", "w-full", "space-y-8")
            }) {
                Div({ classes("lg:hidden", "flex", "flex-col", "items-center", "mb-8") }) {
                    Div({
                        classes("w-20", "h-20", "bg-emerald-50", "rounded-full", "flex", "items-center", "justify-center", "mb-4", "shadow-md")
                    }) {
                        I({ classes("fas", "fa-seedling", "text-white", "text-3xl") })
                    }
                    H2({ classes("text-3xl", "font-bold", "text-emerald-800") }) {
                        Text("Garden Manager")
                    }
                }

                Div {
                    H2({ classes("text-3xl", "font-extrabold", "text-emerald-900") }) {
                        Text(if (isLogin) "Dobrodošli nazad" else "Kreirajte račun")
                    }
                    P({ classes("mt-2", "text-emerald-600") }) {
                        Text(if (isLogin) "Molimo unesite svoje podatke za prijavu." else "Pridružite se našoj zajednici baštovana.")
                    }
                }

                if (errorMessage != null) {
                    Div({
                        classes("bg-red-100", "border", "border-red-400", "text-red-700", "px-4", "py-3", "rounded-xl", "relative")
                    }) {
                        Span({ classes("block", "sm:inline") }) { Text(errorMessage!!) }
                    }
                }

                Form(action = null, attrs = {
                    classes("mt-8", "space-y-6")
                }) {
                    Div({ classes("rounded-md", "shadow-sm", "space-y-4") }) {
                        Div {
                            Label(attrs = {
                                classes("block", "text-sm", "font-medium", "text-emerald-700", "mb-1")
                            }) { Text("Email / Korisničko ime") }
                            Input(InputType.Text) {
                                classes(
                                    "appearance-none", "rounded-xl", "relative", "block", "w-full", "px-4", "py-3", "border", "border-emerald-200", "placeholder-emerald-300", "text-emerald-900", "focus:outline-none", "focus:ring-2", "focus:ring-emerald-500", "focus:border-transparent", "transition-all"
                                )
                                placeholder("Unesite vaš email")
                                value(email)
                                onInput { email = it.value }
                            }
                        }

                        Div {
                            Label(attrs = {
                                classes("block", "text-sm", "font-medium", "text-emerald-700", "mb-1")
                            }) { Text("Lozinka") }
                            Input(InputType.Password) {
                                classes(
                                    "appearance-none", "rounded-xl", "relative", "block", "w-full", "px-4", "py-3", "border", "border-emerald-200", "placeholder-emerald-300", "text-emerald-900", "focus:outline-none", "focus:ring-2", "focus:ring-emerald-500", "focus:border-transparent", "transition-all"
                                )
                                placeholder("••••••••")
                                value(password)
                                onInput { password = it.value }
                            }
                        }
                    }

                    Button(attrs = {
                        val buttonClasses = mutableListOf(
                            "group", "relative", "w-full", "flex", "justify-center", "py-3", "px-4", "border", "border-transparent", "text-md", "font-bold", "rounded-xl", "text-white", "bg-emerald-600", "hover:bg-emerald-700", "focus:outline-none", "focus:ring-2", "focus:ring-offset-2", "focus:ring-emerald-500", "transition-all", "duration-200", "shadow-lg", "shadow-emerald-200"
                        )
                        if (isLoading) buttonClasses.add("opacity-50")
                        classes(buttonClasses)

                        if (isLoading) disabled()
                        onClick { event ->
                            event.preventDefault()
                            if (email.isNotBlank() && password.isNotBlank()) {
                                isLoading = true
                                errorMessage = null
                                val payload = GardenUserRequest(email, password)
                                if (isLogin) {
                                    AuthService.login(payload) { state ->
                                        when (state) {
                                            is ResultState.Success -> {
                                                isLoading = false
                                                val token = state.data.data?.token ?: ""
                                                val refreshToken = state.data.data?.refreshToken ?: ""
                                                if (token.isNotBlank()) {
                                                    localStorage.setItem(GardenHandler.JWT, token)
                                                    localStorage.setItem(GardenHandler.REFRESH_JWT, refreshToken)
                                                    GardenHttpClient.token = token
                                                    onLoginSuccess()
                                                } else {
                                                    errorMessage = "Token nije primljen"
                                                }
                                            }
                                            is ResultState.Error -> {
                                                isLoading = false
                                                errorMessage = state.message
                                            }
                                            is ResultState.Loading -> isLoading = true
                                        }
                                    }
                                } else {
                                    AuthService.register(payload) { state ->
                                        when (state) {
                                            is ResultState.Success -> {
                                                showSnackbar("Registracija uspješna! Molimo prijavite se.", SnackbarType.SUCCESS)
                                                isLogin = true
                                                isLoading = false
                                            }
                                            is ResultState.Error -> {
                                                errorMessage = state.message
                                                isLoading = false
                                            }
                                            is ResultState.Loading -> isLoading = true
                                        }
                                    }
                                }
                            }
                        }
                    }) {
                        Text(if (isLoading) "Obrada..." else if (isLogin) "Prijavi se" else "Registruj se")
                    }
                }

                Div({ classes("text-center", "mt-6") }) {
                    A(href = "#", attrs = {
                        classes("font-semibold", "text-emerald-600", "hover:text-emerald-500", "transition-colors")
                        onClick { isLogin = !isLogin }
                    }) {
                        Text(if (isLogin) "Nemate račun? Registrujte se" else "Već imate račun? Prijavite se")
                    }
                }
            }
        }
    }
}
